<template>
  <div class="page">
    <div class="bg-grid" />
    <TopBar />
    <ProjectNav :project="project" />

    <main class="content">
      <div class="page-header">
        <SectionHeader label="Knowledge Base" :meta="`${total} documents`" />
        <button class="add-btn" @click="openAddModal">+ ADD ENTRY</button>
      </div>

      <div v-if="docs.length === 0" class="empty-state">
        <p class="empty-title">No documents yet</p>
        <p class="empty-body">Knowledge is added automatically as the system learns from feedback and pipeline runs. You can also add entries manually.</p>
      </div>

      <div v-else class="doc-table">
        <div class="table-header">
          <span class="col-content">CONTENT</span>
          <span class="col-source">SOURCE</span>
          <span class="col-confidence">CONFIDENCE</span>
          <span class="col-count">USES</span>
          <span class="col-date">ADDED</span>
          <span class="col-actions" />
        </div>
        <div
          v-for="doc in docs"
          :key="doc.id"
          class="table-row"
          @click="openEdit(doc)"
        >
          <span class="col-content doc-content">{{ truncate(doc.content, 120) }}</span>
          <span class="col-source">
            <span class="source-badge" :class="sourceColor(doc.source)">{{ formatSource(doc.source) }}</span>
          </span>
          <span class="col-confidence">
            <span class="confidence-badge" :class="doc.confidence">{{ doc.confidence }}</span>
          </span>
          <span class="col-count">{{ doc.reinforcement_count }}</span>
          <span class="col-date">{{ formatDate(doc.created_at) }}</span>
          <span class="col-actions" @click.stop>
            <button class="delete-btn" title="Delete" @click="confirmDelete(doc)">✕</button>
          </span>
        </div>
      </div>
    </main>

    <!-- ── Add Entry Modal ────────────────────────────────────────────── -->
    <div v-if="addOpen" class="modal-backdrop" @click.self="closeAddModal">
      <div class="modal modal-wide">
        <div class="modal-header">
          <span class="modal-title">ADD TO KNOWLEDGE BASE</span>
          <button class="modal-close" @click="closeAddModal">✕</button>
        </div>

        <!-- Tab bar -->
        <div class="tab-bar">
          <button
            class="tab-btn"
            :class="{ active: addTab === 'direct' }"
            @click="addTab = 'direct'"
          >DIRECT</button>
          <button
            class="tab-btn"
            :class="{ active: addTab === 'import' }"
            @click="addTab = 'import'"
          >AI IMPORT</button>
        </div>

        <!-- DIRECT tab -->
        <template v-if="addTab === 'direct'">
          <div class="modal-body">
            <p class="tab-hint">Type a single piece of knowledge. Goes in immediately — use this when you know exactly what you want to add.</p>
            <label class="field-label">CONTENT</label>
            <textarea
              v-model="directForm.content"
              class="field-textarea"
              rows="5"
              placeholder="e.g. Product SKU 4819 is Arla Øko Sødmælk 1L. Fat content: 3.5%. Gluten-free."
              spellcheck="true"
            />
            <label class="field-label" style="margin-top:14px">CONFIDENCE</label>
            <select v-model="directForm.confidence" class="field-select">
              <option value="high">high — definite fact</option>
              <option value="medium">medium — likely correct</option>
              <option value="low">low — uncertain</option>
            </select>
            <p v-if="directError" class="field-error">{{ directError }}</p>
            <p v-if="directSaved" class="field-success">✓ Document stored.</p>
          </div>
          <div class="modal-footer">
            <button class="btn-ghost" @click="closeAddModal">CANCEL</button>
            <button
              class="btn-primary"
              :disabled="directSaving || !directForm.content.trim()"
              @click="saveDirectEntry"
            >{{ directSaving ? 'STORING…' : 'ADD DOCUMENT' }}</button>
          </div>
        </template>

        <!-- AI IMPORT tab -->
        <template v-else>
          <div class="modal-body">
            <p class="tab-hint">Paste raw content — product specs, FAQs, docs, a list of facts. The AI reads it, extracts individual relevant facts, and embeds each one separately for accurate retrieval.</p>
            <label class="field-label">RAW CONTENT</label>
            <textarea
              v-model="importForm.content"
              class="field-textarea"
              rows="10"
              placeholder="Paste product descriptions, FAQ entries, documentation, or any raw text…"
              spellcheck="false"
            />
            <p class="import-hint">
              Split by blank line → each paragraph becomes one candidate fact. Single-line text → split by newline.
            </p>
            <p v-if="importError" class="field-error">{{ importError }}</p>
            <JobPoller
              v-if="importJobId"
              :job-id="importJobId"
              :done-message="importDoneMessage"
              @done="onImportDone"
              @failed="onImportFailed"
            />
          </div>
          <div class="modal-footer">
            <button class="btn-ghost" @click="closeAddModal">{{ importJobId && importFinished ? 'CLOSE & REFRESH' : 'CANCEL' }}</button>
            <button
              v-if="!importFinished"
              class="btn-primary"
              :disabled="importSaving || importJobId || !importForm.content.trim()"
              @click="startImport"
            >{{ importSaving ? 'QUEUING…' : `IMPORT WITH AI` }}</button>
          </div>
        </template>
      </div>
    </div>

    <!-- ── Edit Modal ─────────────────────────────────────────────────── -->
    <div v-if="editTarget" class="modal-backdrop" @click.self="editTarget = null">
      <div class="modal modal-wide">
        <div class="modal-header">
          <span class="modal-title">EDIT DOCUMENT</span>
          <button class="modal-close" @click="editTarget = null">✕</button>
        </div>
        <div class="modal-body">
          <label class="field-label">CONTENT</label>
          <textarea
            v-model="editForm.content"
            class="field-textarea"
            rows="6"
            spellcheck="true"
          />
          <label class="field-label" style="margin-top:14px">CONFIDENCE</label>
          <select v-model="editForm.confidence" class="field-select">
            <option value="high">high — definite fact</option>
            <option value="medium">medium — likely correct</option>
            <option value="low">low — uncertain</option>
          </select>
          <p class="field-hint">Saving sets confidence to your chosen level.</p>
          <p v-if="editError" class="field-error">{{ editError }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="editTarget = null">CANCEL</button>
          <button
            class="btn-primary"
            :disabled="editSaving || !editForm.content.trim()"
            @click="saveEdit"
          >{{ editSaving ? 'SAVING…' : 'SAVE CHANGES' }}</button>
        </div>
      </div>
    </div>

    <!-- ── Delete Confirm ─────────────────────────────────────────────── -->
    <div v-if="deleteTarget" class="modal-backdrop" @click.self="deleteTarget = null">
      <div class="modal modal-sm">
        <div class="modal-header">
          <span class="modal-title">DELETE DOCUMENT</span>
          <button class="modal-close" @click="deleteTarget = null">✕</button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">
            Delete this knowledge entry? It will no longer be retrieved during pipeline runs.
          </p>
          <pre class="confirm-preview">{{ truncate(deleteTarget?.content, 140) }}</pre>
          <p v-if="deleteError" class="field-error">{{ deleteError }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">CANCEL</button>
          <button class="btn-danger" :disabled="deleting" @click="deleteDoc">
            {{ deleting ? 'DELETING…' : 'DELETE' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue"
import { router } from "@inertiajs/vue3"
import TopBar from "../../components/TopBar.vue"
import ProjectNav from "../../components/ProjectNav.vue"
import SectionHeader from "../../components/SectionHeader.vue"
import JobPoller from "../../components/JobPoller.vue"

const props = defineProps({
  project:   { type: Object, required: true },
  docs:      { type: Array,  default: () => [] },
  total:     { type: Number, default: 0 },
  user_role: { type: String, default: "viewer" },
})

// ── helpers ───────────────────────────────────────────────────────────────────
function truncate(text, max) {
  if (!text) return ""
  return text.length > max ? text.slice(0, max) + "…" : text
}
function formatDate(dateStr) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })
}
function formatSource(source) {
  return source.replace(/_/g, " ")
}
function sourceColor(source) {
  const map = { auto_learned: "amber", human_correction: "green", api_upload: "blue", self_eval: "purple" }
  return map[source] ?? "dim"
}
async function apiCall(method, path, body = null) {
  const token = document.querySelector("meta[name='csrf-token']")?.content
  const opts = { method, headers: { "Content-Type": "application/json", "X-CSRF-Token": token } }
  if (body) opts.body = JSON.stringify(body)
  const res = await fetch(path, opts)
  return { status: res.status, data: await res.json() }
}
function reload() {
  router.visit(`/projects/${props.project.uuid}/rag`, { replace: true })
}

// ── Add modal ─────────────────────────────────────────────────────────────────
const addOpen = ref(false)
const addTab  = ref("direct")

function openAddModal() {
  directForm.value   = { content: "", confidence: "high" }
  directError.value  = ""
  directSaved.value  = false
  importForm.value   = { content: "" }
  importError.value  = ""
  importJobId.value  = null
  importFinished.value = false
  addTab.value       = "direct"
  addOpen.value      = true
}
function closeAddModal() {
  if (importFinished.value || directSaved.value) { reload(); return }
  addOpen.value = false
}

// ── Direct add ────────────────────────────────────────────────────────────────
const directForm  = ref({ content: "", confidence: "high" })
const directSaving = ref(false)
const directError  = ref("")
const directSaved  = ref(false)

async function saveDirectEntry() {
  directSaving.value = true
  directError.value  = ""
  directSaved.value  = false
  try {
    const { status, data } = await apiCall(
      "POST",
      `/api/v1/projects/${props.project.uuid}/rag`,
      directForm.value
    )
    if (status === 201) {
      directSaved.value = true
      directForm.value  = { content: "", confidence: "high" }
      setTimeout(reload, 800)
    } else {
      directError.value = data?.message || "Failed to store document."
    }
  } catch { directError.value = "Network error." }
  finally { directSaving.value = false }
}

// ── AI Import ─────────────────────────────────────────────────────────────────
const importForm     = ref({ content: "" })
const importSaving   = ref(false)
const importError    = ref("")
const importJobId    = ref(null)
const importFinished = ref(false)
const importDoneMessage = ref("Import complete")

async function startImport() {
  importSaving.value = true
  importError.value  = ""
  try {
    const { status, data } = await apiCall(
      "POST",
      `/api/v1/projects/${props.project.uuid}/rag/import`,
      { content: importForm.value.content }
    )
    if (status === 202) {
      importJobId.value = data.job_id
    } else {
      importError.value = data?.message || "Failed to start import."
    }
  } catch { importError.value = "Network error." }
  finally { importSaving.value = false }
}
function onImportDone(jobData) {
  const r = jobData?.result ?? {}
  const stored  = r.stored  ?? 0
  const skipped = r.skipped ?? 0
  importDoneMessage.value = `${stored} fact${stored !== 1 ? "s" : ""} stored, ${skipped} skipped`
  importFinished.value = true
}
function onImportFailed(jobData) {
  importError.value = jobData?.error || "Import job failed."
  importJobId.value = null
}

// ── Edit ──────────────────────────────────────────────────────────────────────
const editTarget = ref(null)
const editForm   = ref({ content: "", confidence: "high" })
const editSaving = ref(false)
const editError  = ref("")

function openEdit(doc) {
  editTarget.value = doc
  editForm.value   = { content: doc.content, confidence: doc.confidence }
  editError.value  = ""
}
async function saveEdit() {
  editSaving.value = true
  editError.value  = ""
  try {
    const { status, data } = await apiCall(
      "PATCH",
      `/api/v1/projects/${props.project.uuid}/rag/${editTarget.value.id}`,
      editForm.value
    )
    if (status === 200) { editTarget.value = null; reload() }
    else editError.value = data?.message || "Failed to save."
  } catch { editError.value = "Network error." }
  finally { editSaving.value = false }
}

// ── Delete ────────────────────────────────────────────────────────────────────
const deleteTarget = ref(null)
const deleting     = ref(false)
const deleteError  = ref("")

function confirmDelete(doc) {
  deleteTarget.value = doc
  deleteError.value  = ""
}
async function deleteDoc() {
  deleting.value    = true
  deleteError.value = ""
  try {
    const { status, data } = await apiCall(
      "DELETE",
      `/api/v1/projects/${props.project.uuid}/rag/${deleteTarget.value.id}`
    )
    if (status === 200) { deleteTarget.value = null; reload() }
    else deleteError.value = data?.message || "Failed to delete."
  } catch { deleteError.value = "Network error." }
  finally { deleting.value = false }
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: #0a0a0b;
  position: relative;
}
.bg-grid {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}
.content { position: relative; z-index: 1; padding: 20px 24px; }

.page-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px;
}
.add-btn {
  font-family: var(--font-mono); font-size: 10px; font-weight: 700;
  letter-spacing: 0.8px; padding: 6px 14px; border-radius: 4px;
  border: 1px solid rgba(245, 166, 35, 0.4);
  background: rgba(245, 166, 35, 0.08); color: #f5a623;
  cursor: pointer; transition: background 0.15s, border-color 0.15s;
}
.add-btn:hover { background: rgba(245, 166, 35, 0.16); border-color: rgba(245, 166, 35, 0.65); }

/* ── Empty state ── */
.empty-state {
  padding: 48px; text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 8px;
  background: rgba(255, 255, 255, 0.025);
}
.empty-title { font-family: var(--font-display); font-size: 16px; font-weight: 700; color: #e8e6e3; margin: 0 0 8px; }
.empty-body  { font-family: var(--font-mono); font-size: 12px; color: rgba(232, 230, 227, 0.45); margin: 0; }

/* ── Table ── */
.doc-table {
  border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 8px;
  overflow: hidden; background: rgba(255, 255, 255, 0.025);
}
.table-header {
  display: grid;
  grid-template-columns: 1fr 120px 90px 50px 90px 40px;
  padding: 10px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-family: var(--font-mono); font-size: 9px; font-weight: 500;
  letter-spacing: 1.5px; text-transform: uppercase;
  color: rgba(232, 230, 227, 0.25);
}
.table-row {
  display: grid;
  grid-template-columns: 1fr 120px 90px 50px 90px 40px;
  align-items: center;
  padding: 11px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer; transition: background 0.15s;
}
.table-row:hover { background: rgba(245, 166, 35, 0.06); }

.doc-content {
  font-family: var(--font-mono); font-size: 11px; color: rgba(232, 230, 227, 0.7);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-right: 12px;
}
.source-badge, .confidence-badge {
  display: inline-block; font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 0.8px; text-transform: uppercase; padding: 2px 7px; border-radius: 3px;
}
.source-badge.amber  { background: rgba(245, 166, 35, 0.18);  color: #f5a623; }
.source-badge.green  { background: rgba(74, 222, 128, 0.15);  color: #4ade80; }
.source-badge.blue   { background: rgba(96, 165, 250, 0.15);  color: #60a5fa; }
.source-badge.purple { background: rgba(192, 132, 252, 0.15); color: #c084fc; }
.source-badge.dim    { background: rgba(255, 255, 255, 0.06); color: rgba(232, 230, 227, 0.45); }
.confidence-badge.high   { background: rgba(74, 222, 128, 0.15);  color: #4ade80; }
.confidence-badge.medium { background: rgba(245, 166, 35, 0.18);  color: #f5a623; }
.confidence-badge.low    { background: rgba(248, 113, 113, 0.15); color: #f87171; }
.col-count, .col-date {
  font-family: var(--font-mono); font-size: 11px; color: rgba(232, 230, 227, 0.45);
}
.col-actions { display: flex; align-items: center; justify-content: flex-end; }
.delete-btn {
  font-family: var(--font-mono); font-size: 10px; font-weight: 700;
  padding: 3px 7px; border-radius: 3px; border: 1px solid transparent;
  background: transparent; color: rgba(248, 113, 113, 0.3);
  cursor: pointer; transition: all 0.15s; flex-shrink: 0;
}
.delete-btn:hover { color: #f87171; border-color: rgba(248, 113, 113, 0.35); background: rgba(248, 113, 113, 0.08); }

/* ── Modal ── */
.modal-backdrop {
  position: fixed; inset: 0; z-index: 50;
  background: rgba(0, 0, 0, 0.72);
  display: flex; align-items: center; justify-content: center; padding: 24px;
}
.modal {
  background: #141416; border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px; width: 100%; max-width: 480px;
  display: flex; flex-direction: column; max-height: 90vh; overflow: hidden;
}
.modal.modal-sm   { max-width: 380px; }
.modal.modal-wide { max-width: 600px; }
.modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 20px 14px; border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}
.modal-title { font-family: var(--font-mono); font-size: 11px; font-weight: 700; letter-spacing: 1.2px; color: rgba(232, 230, 227, 0.6); }
.modal-close { background: none; border: none; color: rgba(232, 230, 227, 0.3); font-size: 14px; cursor: pointer; padding: 0; transition: color 0.15s; }
.modal-close:hover { color: #e8e6e3; }
.modal-body { padding: 20px; display: flex; flex-direction: column; overflow-y: auto; flex: 1; }
.modal-footer {
  padding: 14px 20px; border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex; justify-content: flex-end; gap: 10px; flex-shrink: 0;
}

/* ── Tabs ── */
.tab-bar {
  display: flex; border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding: 0 20px; gap: 2px; flex-shrink: 0;
}
.tab-btn {
  font-family: var(--font-mono); font-size: 10px; font-weight: 700; letter-spacing: 1.2px;
  padding: 10px 14px; background: none; border: none;
  border-bottom: 2px solid transparent; color: rgba(232, 230, 227, 0.35);
  cursor: pointer; transition: all 0.15s; margin-bottom: -1px;
}
.tab-btn:hover { color: rgba(232, 230, 227, 0.65); }
.tab-btn.active { color: #f5a623; border-bottom-color: #f5a623; background: rgba(245, 166, 35, 0.06); }

/* ── Fields ── */
.tab-hint {
  font-family: var(--font-mono); font-size: 10px; color: rgba(232, 230, 227, 0.35);
  margin: 0 0 16px; line-height: 1.6;
}
.field-label {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 1.2px; color: rgba(232, 230, 227, 0.35); margin-bottom: 6px; display: block;
}
.field-textarea {
  width: 100%; box-sizing: border-box; resize: vertical;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 5px; padding: 10px 12px;
  font-family: var(--font-mono); font-size: 11px; color: #e8e6e3;
  outline: none; line-height: 1.6; transition: border-color 0.15s;
}
.field-textarea:focus { border-color: rgba(245, 166, 35, 0.5); }
.field-textarea::placeholder { color: rgba(232, 230, 227, 0.15); }
.field-select {
  width: 100%; box-sizing: border-box;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 5px; padding: 9px 12px;
  font-family: var(--font-mono); font-size: 12px; color: #e8e6e3;
  outline: none; appearance: none; cursor: pointer; transition: border-color 0.15s;
}
.field-select:focus { border-color: rgba(245, 166, 35, 0.5); }
.field-hint  { font-family: var(--font-mono); font-size: 10px; color: rgba(232, 230, 227, 0.25); margin-top: 6px; }
.field-error { font-family: var(--font-mono); font-size: 10px; color: #f87171; margin-top: 8px; }
.field-success { font-family: var(--font-mono); font-size: 10px; color: #4ade80; margin-top: 8px; }
.import-hint { font-family: var(--font-mono); font-size: 9px; color: rgba(232, 230, 227, 0.2); margin: 6px 0 0; line-height: 1.5; }

/* ── Delete confirm ── */
.confirm-text {
  font-family: var(--font-body); font-size: 13px; color: rgba(232, 230, 227, 0.7); line-height: 1.5; margin: 0 0 12px;
}
.confirm-preview {
  font-family: var(--font-mono); font-size: 10px; color: rgba(232, 230, 227, 0.4);
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 4px; padding: 10px 12px; white-space: pre-wrap; word-break: break-word; margin: 0;
}

/* ── Buttons ── */
.btn-primary {
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 0.8px; padding: 8px 18px; border-radius: 5px; border: none;
  background: #f5a623; color: #0a0a0b; cursor: pointer; transition: opacity 0.15s;
}
.btn-primary:hover:not(:disabled) { opacity: 0.88; }
.btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-ghost {
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 0.8px; padding: 8px 18px; border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent; color: rgba(232, 230, 227, 0.45); cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}
.btn-ghost:hover { border-color: rgba(255, 255, 255, 0.2); color: rgba(232, 230, 227, 0.7); }
.btn-danger {
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 0.8px; padding: 8px 18px; border-radius: 5px;
  background: rgba(248, 113, 113, 0.15); color: #f87171; cursor: pointer;
  border: 1px solid rgba(248, 113, 113, 0.3); transition: background 0.15s;
}
.btn-danger:hover:not(:disabled) { background: rgba(248, 113, 113, 0.25); }
.btn-danger:disabled { opacity: 0.4; cursor: not-allowed; }
</style>
